import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';

import { Ati } from '@atlassian/ari';
import {
	ExperienceName,
	useAIMateExperienceTracker,
} from '@atlassian/conversation-assistant-instrumentation';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { expValEquals } from '@confluence/feature-experiments';
import { experimentalPromotedSpacesQuery } from '@confluence/legacy-schema-bridge/entry-points/experimentalPromotedSpacesQuery';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore';
import { UniversalCreateWithRovoSpotlight } from '@confluence/onboarding-ai-first-create/entry-points/UniversalCreateWithRovoSpotlight';
import { usePreloadSmartCreationModal } from '@confluence/smart-create/entry-points/usePreloadSmartCreationModal';
import { HOME } from '@confluence/named-routes/entry-points/routes';

import type { UseMenuItemProps, UseMenuItemReturnProps } from '.';

import { useUniversalCreateState } from '../useUniversalCreate';
import { ItemTitleWithLozenge } from '../ItemTitle';
import type {
	GetPromotedSpacesQueryVariables,
	GetPromotedSpacesQuery as GetPromotedSpacesQueryType,
} from '../../queries/__types__/GetPromotedSpacesQuery';
// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { GetPromotedSpacesQuery } from '../../queries/GetPromotedSpacesQuery.experimentalgraphql';

const i18n = defineMessages({
	smartCreateTitle: {
		id: 'create-blank-fabric-page.create.content.smart-create-with-ai.title',
		defaultMessage: 'Create with Rovo',
		description: 'title indicating that this option will trigger an AI guided creation workflow',
	},
});

const getSpaceKeyForCreation = async (): Promise<string> => {
	const spaceKeyRequest: GetPromotedSpacesQueryVariables = {
		promotedSpaceKey: undefined,
	};

	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	const spaceKeyResponse: GetPromotedSpacesQueryType = expValEquals(
		'confluence_deprecate_experimental_schema_exp_2',
		'isEnabled',
		true,
	)
		? (
				await experimentalPromotedSpacesQuery({
					variables: { ...spaceKeyRequest, promotedSpacesLimit: 1, otherSpacesLimit: 1 },
				})
			).data
		: // eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			(
				await getApolloClient().query({
					query: GetPromotedSpacesQuery,
					variables: spaceKeyRequest,
				})
			).data;

	return spaceKeyResponse?.experimentalPromotedSpaces?.nodes?.[0]?.id ?? '';
};

/**
 * This is a menu item for an internal demo/spike of a new AI powered creation workflow.
 */
export function useAISmartCreateItem({
	spaceKey,
	onClick,
	source,
	excludeContext,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();
	const { openModal } = useSmartCreationModalActions();
	const { isOpen } = useUniversalCreateState();
	const experienceTracker = useAIMateExperienceTracker();
	const preloadSmartCreationModal = usePreloadSmartCreationModal();

	const [fetchedSpaceKey, setFetchedSpaceKey] = useState<string>('');
	const [spaceKeyLoading, setSpaceKeyLoading] = useState<boolean>(true);

	// Promoted Spaces query should be cached, but we do have a race condition here for global create where there
	// can potentially be missing space key. This is fine for the early spike stage.
	useEffect(() => {
		setSpaceKeyLoading(true);
		void getSpaceKeyForCreation()
			.then(setFetchedSpaceKey)
			.finally(() => setSpaceKeyLoading(false));
	}, []);

	useEffect(() => {
		if (isOpen) {
			preloadSmartCreationModal();
		}
	}, [isOpen, preloadSmartCreationModal]);

	const handleOpenCreateWithRovoExperience = useCallback(() => {
		experienceTracker.start({
			name: ExperienceName.AIFC_LOAD_STAGING_AREA_MODAL,
			id: ExperienceName.AIFC_LOAD_STAGING_AREA_MODAL,
			attributes: {
				product: 'confluence',
				source,
			},
		});

		if (excludeContext) {
			openModal(source);
		} else {
			openModal(source, spaceKey ?? fetchedSpaceKey);
		}
	}, [excludeContext, experienceTracker, fetchedSpaceKey, openModal, source, spaceKey]);

	const handleItemOnClick = useCallback(
		(e: any, actionSubjectId: string, attributes?: Record<string, string>) => {
			onClick(actionSubjectId, attributes);

			// Check if this is a modifier key click and if so, let the default
			// link behavior handle opening a new tab to home route with
			// smart create modal open
			if (e?.metaKey || e?.ctrlKey) {
				return;
			}

			// If this is not a modifier key click, prevent default link
			// navigation and instead open up the modal
			e.preventDefault();

			// Open the modal
			handleOpenCreateWithRovoExperience();
		},
		[onClick, handleOpenCreateWithRovoExperience],
	);

	const CreateWithRovoSpotlightWrapper = useMemo(() => {
		return (props: { children?: () => React.ReactNode }) => (
			<UniversalCreateWithRovoSpotlight
				onClick={() => handleOpenCreateWithRovoExperience()}
				isLoading={spaceKeyLoading}
				source={source}
			>
				{props.children && props.children()}
			</UniversalCreateWithRovoSpotlight>
		);
	}, [handleOpenCreateWithRovoExperience, source, spaceKeyLoading]);

	// For all `cmd` / `ctrl` + click, we want to open the home route
	// in a new tab with the smart creation modal open
	const href = HOME.toUrl({}, { query: { openCreateWithRovo: 'true' } });

	return {
		ati: Ati.parse('ati:cloud:confluence:page'), // todo: this is incorrect, we don't know the type identifier yet
		children: (
			<ItemTitleWithLozenge useLabsLozenge>
				{formatMessage(i18n.smartCreateTitle)}
			</ItemTitleWithLozenge>
		),
		Wrapper: CreateWithRovoSpotlightWrapper,
		onClick: (e: any) => handleItemOnClick(e, 'createAISmartCreatePopoverItem'),
		href,
		elemBefore: AiGenerativeTextIcon,
		testId: `create-menu-link-item`,
	};
}
